import clsx from 'clsx';
import {Field, Form, Formik, FormikErrors, FormikTouched} from 'formik';
import {FormikHelpers} from 'formik/dist/types';
import {QRCodeCanvas} from 'qrcode.react';
import React, {useEffect, useMemo, useState} from 'react';
import Toast from 'react-bootstrap/Toast';
import {useTranslation} from 'react-i18next';
import parse from 'url-parse';
import * as Yup from 'yup';
import {KTCard, KTCardBody, KTIcon, KTSVG,} from '../../../../_mesomb/helpers';
import i18n from '../../../../_mesomb/i18n';
import ButtonIndicator from '../../../../_mesomb/partials/components/ButtonIndicator.tsx';
import {addressFieldSchema} from '../../../../_mesomb/partials/form/AddressInput.tsx';
import {contactFieldSchema} from '../../../../_mesomb/partials/form/ContactField.tsx';
import FormField from '../../../../_mesomb/partials/form/FormField.tsx';
import {fullNameFieldSchema} from '../../../../_mesomb/partials/form/FullNameField.tsx';
import {camelize, countryFlagUrl} from '../../../../_mesomb/utils';
import countries from '../../../../_mesomb/utils/countries.ts';
import fonts from '../../../../data/branding_fonts.json';
import {
  AppConfig,
  ApplicationModel,
  BrandingSettings,
  ConsentCollection,
  CustomerModel,
  CustomText,
  Discounts,
  EscrowFeeEngine,
  EscrowFeePayer,
  PayCallToActionType,
  PaymentCustomField,
  PaymentLinkProduct,
  SecurePayMode,
} from '../../../../types';
import providerLogos from "../../../../_mesomb/utils/providerLogos.ts";

const statusBadgeClass: Record<string, string> = {
  created: 'badge-light-primary',
  open: 'badge-light-primary',
  paid: 'badge-light-success',
  expired: 'badge-light-danger',
  completed: 'badge-light-success',
};

const productSchema = Yup.object().shape({
  product: Yup.string().required(i18n.t('Field_is_required')),
  quantity: Yup.number()
    .required(i18n.t('Field_is_required'))
    .min(1, i18n.t('Invalid quantity')),
});

const formSchema = Yup.object().shape({
  country: Yup.string().required(i18n.t('Field_is_required')),
  service: Yup.string()
    .oneOf([
      'AIRTEL',
      'HALOTEL',
      'MESOMB',
      'MOOV',
      'MTN',
      'ORANGE',
      'TNM',
      'VODACOM',
      'WAVE',
      'YAS',
      'ZAMTEL',
      'MPESA',
      'YOOMEE',
    ])
    .required(i18n.t('Please select a payment provider')),
  payer: Yup.string()
    .required(i18n.t('Field_is_required'))
    .matches(/^[+]?\d{8,12}$/, i18n.t('Invalid phone number')),
  contact: contactFieldSchema,
  full_name: fullNameFieldSchema,
  address: addressFieldSchema,
  line_items: Yup.array().of(productSchema),
  amount: Yup.number()
    .required(i18n.t('Field_is_required'))
    .min(1, i18n.t('Invalid amount')),
  custom_fields: Yup.array().of(Yup.string()),
  terms_of_service: Yup.boolean().default(false),
  promotions: Yup.boolean().default(false),
  coupon: Yup.string().nullable(),
  promotion_code: Yup.string().nullable(),
  escrow_mode: Yup.boolean().default(false),
});
type CheckoutFormValues = Yup.InferType<typeof formSchema>;

export interface PayWidgetProps {
  title: string;
  subtitle?: string;
  description?: string;
  image?: string;
  currency: string;
  defaultTotalAmount?: number;
  status?: string;
  isDemo?: boolean;
  configs: AppConfig;
  application?: ApplicationModel;
  line_items: PaymentLinkProduct[];
  metadata?: Record<string, string>;
  amountOption: {
    type: 'freeType' | 'products';
    editable?: boolean;
    suggested?: number;
  };
  extraFees?: {
    type?: 'FIXED' | 'PERCENTAGE';
    value?: number;
    enabled: boolean;
    reason?: string;
  };
  deliveryFees?: number;
  options?: {
    collectCustomerInfo?: boolean;
    requiredContactInfo?: boolean;
    collectCustomerAddress?: boolean;
    customFields?: PaymentCustomField[];
    consentCollection?: ConsentCollection;
    customText?: CustomText;
    discounts?: Discounts;
  };
  customer?: CustomerModel;
  brandingSettings?: BrandingSettings;
  callToAction: PayCallToActionType;
  object: 'checkout' | 'link';
  onCancel?: () => Promise<void>;
  onSubmit: (
    values: CheckoutFormValues,
    formikHelpers: FormikHelpers<CheckoutFormValues>
  ) => void | Promise<unknown>;
  escrow?: {
    pay_mode?: SecurePayMode;
    delivery_required?: boolean;
    fee_payer?: EscrowFeePayer;
    terms_accepted?: boolean;
    commitment_fee?: number;
  };
  escrowFeeEngine?: EscrowFeeEngine;
  environment?: 'production' | 'sandbox';
}

function googleFontUrl(fontName: string) {
  const family = fontName.trim().replace(/\s+/g, '+');

  return `https://fonts.googleapis.com/css2?family=${family}:wght@300;400;500;600;700&display=swap`;
}

function loadGoogleFont(fontKey: string) {
  if (!fontKey || fontKey === 'default') {
    return;
  }

  const fontName = fonts.find((f) => f.key === fontKey)?.name;

  if (!fontName) {
    return;
  }

  const linkId = `google-font-${fontKey}`;

  if (document.getElementById(linkId)) {
    return;
  }

  const link = document.createElement('link');
  link.id = linkId;
  link.rel = 'stylesheet';
  link.referrerPolicy = 'no-referrer';
  link.href = googleFontUrl(fontName);

  document.head.appendChild(link);
}

function PayWidget({
  title,
  subtitle,
  description: widgetDescription,
  image,
  currency,
  defaultTotalAmount,
  status,
  application,
  isDemo,
  configs,
  line_items,
  metadata,
  amountOption,
  extraFees,
  options,
  customer,
  brandingSettings,
  callToAction,
  onSubmit,
  object,
  onCancel,
  escrow,
  deliveryFees,
  escrowFeeEngine,
  environment,
}: PayWidgetProps) {
  const { t } = useTranslation();
  const [isCancelling, setIsCancelling] = useState(false)

  const computeProductsTotal = (
    productValues: CheckoutFormValues['line_items']
  ) => {
    return (productValues ?? []).reduce((acc, prod, i) => {
      const unit = line_items?.[i]?.unit_amount ?? 0;
      return acc + prod.quantity * unit;
    }, 0);
  };

  const computeExtraFeeAmount = (subtotal: number): number => {
    if (!extraFees?.enabled || !extraFees.value) return 0;
    if (extraFees.type === 'PERCENTAGE') {
      return (subtotal * extraFees.value) / 100;
    }
    return extraFees.value;
  };

  const computeEscrowFeeAmount = (
    subtotal: number,
    securePayEnabled: boolean
  ): number => {
    if (
      !escrowFeeEngine ||
      !securePayEnabled ||
      (escrow?.fee_payer ?? 'seller') == 'seller'
    )
      return 0;
    const v = Math.min(Math.max(escrowFeeEngine.min, subtotal * escrowFeeEngine.rate), escrowFeeEngine.max);
    return escrow?.fee_payer == 'buyer' ? v : v / 2;
  };

  useEffect(() => {
    if (brandingSettings?.font_family && brandingSettings?.font_family != '') {
      loadGoogleFont(brandingSettings?.font_family);
    }
  }, [brandingSettings?.font_family]);

  const paymentPricing = configs.payment_pricing;

  const [totalAmount, setTotalAmount] = useState<number | undefined>(
    object == 'link' && amountOption.type == 'products'
      ? (() => {
          const initialLineItems = (line_items ?? []).map((product) => ({
              product: String(product.product.id),
              quantity: product.quantity,
            })) as CheckoutFormValues['line_items'];
          const subtotal = computeProductsTotal(initialLineItems);
          return subtotal + computeExtraFeeAmount(subtotal);
        })()
      : (defaultTotalAmount ?? 0) +
          computeExtraFeeAmount(defaultTotalAmount ?? 0)
  );
  const [showQrCode, setShowQrCode] = useState(false);
  const [showToast, setShowToast] = useState<{
    display: boolean;
    service?: string;
  }>({ display: false });
  const [isLinkCopied, setIsLinkCopied] = useState(false);

  // const isExpired = useMemo(() => {
  //   if (!session) return false;
  //   if (session.is_expired) return true;
  //   if (!session.expires_at) return false;
  //   return new Date(session.expires_at).getTime() <= Date.now();
  // }, [session]);
  const isExpired = false;

  const excludes: (keyof CheckoutFormValues)[] = [];
  if (!options?.collectCustomerInfo) excludes.push('full_name');
  if (!options?.collectCustomerAddress) excludes.push('address');
  if (!options?.requiredContactInfo) excludes.push('contact');
  if (amountOption.type !== 'products') excludes.push('line_items');
  if (amountOption.type !== 'freeType') excludes.push('amount');

  const pageUrl = useMemo(() => location.href, []);

  const parsed = parse(location.href, true);
  const urlParams = parsed.query;

  const widgetTheme = useMemo<React.CSSProperties>(
    () => ({
      fontFamily: brandingSettings?.font_family,
      backgroundColor: brandingSettings?.background_color,
    }),
    [brandingSettings?.font_family, brandingSettings?.background_color]
  );

  const cardTheme = useMemo<React.CSSProperties>(
    () => ({
      borderStyle: brandingSettings?.border_style as
        | React.CSSProperties['borderStyle']
        | undefined,
      borderColor: brandingSettings?.primary_color,
    }),
    [brandingSettings?.border_style, brandingSettings?.primary_color]
  );

  const accentStyle = useMemo<React.CSSProperties | undefined>(() => {
    if (!brandingSettings?.primary_color) return undefined;
    return { color: brandingSettings.primary_color };
  }, [brandingSettings?.primary_color]);

  const accentBadgeStyle = useMemo<React.CSSProperties | undefined>(() => {
    if (!brandingSettings?.primary_color) return undefined;
    return {
      backgroundColor: brandingSettings.primary_color,
      borderColor: brandingSettings.primary_color,
      color: '#FFFFFF',
    };
  }, [brandingSettings?.primary_color]);

  const headingStyle = useMemo<React.CSSProperties | undefined>(() => {
    if (!brandingSettings?.primary_color) return undefined;
    return { color: brandingSettings.primary_color };
  }, [brandingSettings?.primary_color]);

  const qrButtonStyle = useMemo<React.CSSProperties | undefined>(() => {
    if (!brandingSettings?.primary_color) return undefined;
    return {
      color: brandingSettings.primary_color,
      borderColor: brandingSettings.primary_color,
    };
  }, [brandingSettings?.primary_color]);

  const stepBadgeStyle = useMemo<React.CSSProperties>(
    () => ({
      minWidth: 24,
      height: 24,
      borderRadius: 999,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: brandingSettings?.primary_color ?? '#009ef7',
      color: '#FFFFFF',
      fontSize: 12,
      fontWeight: 700,
      lineHeight: 1,
    }),
    [brandingSettings?.primary_color]
  );

  const ctaColor =
    brandingSettings?.button_color ?? brandingSettings?.primary_color;

  const ctaLabel = options?.customText?.submit
    ? t(options.customText.submit, { defaultValue: options.customText.submit })
    : t(camelize(callToAction), { defaultValue: 'Pay' });

  const securePayMode = escrow?.pay_mode ?? 'disabled';
  const securePayAvailable =
    securePayMode !== 'disabled' && escrow?.terms_accepted !== false;
  const securePayOptional = securePayAvailable && securePayMode === 'optional';
  const securePayIsMandatory = securePayMode === 'mandatory';
  const securePayFeePayer = escrow?.fee_payer ?? 'buyer';
  const securePayDeliveryRequired = Boolean(escrow?.delivery_required);
  const securePayCommitmentFee = Number(escrow?.commitment_fee ?? 0);

  const securePayModeLabel =
    securePayMode === 'mandatory'
      ? t('Mandatory')
      : securePayMode === 'optional'
        ? t('Optional')
        : t('Disabled');

  const securePayFeePayerLabel =
    securePayFeePayer === 'seller'
      ? t('Seller')
      : securePayFeePayer === 'split'
        ? t('Split')
        : t('Buyer');

  const providerUssd: Record<string, string> = {
    ORANGE: '#150*50#',
    MTN: '*126#',
  };

  const customerPrefill = useMemo(() => {
    if (!customer) {
      return {
        displayName: '',
        email: '',
        phone: '',
        firstName: '',
        lastName: '',
        country: '',
        region: '',
        town: '',
        address: '',
      };
    }

    const firstName = customer.first_name ?? '';
    const lastName = customer.last_name ?? '';
    const displayName =
      customer.fullName ??
      customer.full_name ??
      [firstName, lastName].filter(Boolean).join(' ').trim() ??
      customer.name ??
      '';

    const rawAddress = customer.displayAddress ?? customer.address ?? '';
    const address =
      typeof rawAddress === 'string'
        ? rawAddress
        : (rawAddress as { line1?: string })?.line1 ?? '';

    return {
      displayName,
      email: customer.email ?? '',
      phone: customer.phone ?? '',
      firstName,
      lastName: lastName || customer.name || '',
      country: customer.country ?? '',
      region: customer.region ?? '',
      town: customer.town ?? '',
      address,
    };
  }, [customer]);
  return (
    <>
      <KTCard flush={true} className={'w-lg-1000px'} style={widgetTheme}>
        <KTCardBody>
          <div>
            <div className='row g-6 align-items-stretch'>
              <div className='col-12'>
                <div className='d-flex flex-column flex-lg-row align-items-start align-items-lg-center justify-content-between gap-4'>
                  <div className='d-flex align-items-center gap-3'>
                    <div className='symbol symbol-45px symbol-circle bg-light'>
                      <span className='symbol-label' style={accentStyle}>
                        <KTSVG path='/assets/icons/credit-cart.svg' className='fs-2' />
                      </span>
                    </div>
                    <div className='d-flex flex-column'>
                      <div className='d-flex flex-wrap align-items-center gap-2'>
                        <h2 className='m-0 fw-bold text-gray-900'>{title}</h2>
                        {status && (
                          <span
                            className={clsx(
                              'badge',
                              statusBadgeClass[status] ?? 'badge-light-info'
                            )}
                          >
                            {t(`checkout.status.${status.toLowerCase()}`, {
                              defaultValue: status.toUpperCase(),
                            })}
                          </span>
                        )}
                        {environment === 'sandbox' && (
                          <span className='badge badge-warning'>Sandbox</span>
                        )}
                      </div>

                      {subtitle && (
                        <div className='text-gray-700 fw-semibold mt-1'>
                          {subtitle}
                        </div>
                      )}

                      {widgetDescription && (
                        <p className='text-muted fs-7 mt-2 mb-0' style={{ maxWidth: 680 }}>
                          {widgetDescription}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className='d-flex flex-wrap gap-2'>
                    <button
                      type='button'
                      className='btn btn-light'
                      onClick={() => setShowQrCode((v) => !v)}
                      aria-pressed={showQrCode}
                      style={qrButtonStyle}
                    >
                      <KTSVG path='/assets/icons/barcode.svg' className='fs-2 me-2' />
                      {showQrCode
                        ? t('Hide QR code', { defaultValue: 'Hide QR code' })
                        : t('Show QR code', { defaultValue: 'Show QR code' })}
                    </button>
                  </div>
                </div>
              </div>

              {showQrCode && (
                <div className='col-12'>
                  <div className='alert alert-secondary d-flex flex-column flex-lg-row align-items-start align-items-lg-center justify-content-between gap-4 mb-0'>
                    <div className='d-flex flex-column'>
                      <strong className='text-gray-900'>
                        {t('Scan checkout on mobile', {
                          defaultValue: 'Scan checkout on mobile',
                        })}
                      </strong>
                      <span className='text-muted'>
                        {t(
                          'Open your camera or QR scanner and scan this code to open this checkout page.',
                          {
                            defaultValue:
                              'Open your camera or QR scanner and scan this code to open this checkout page.',
                          }
                        )}
                      </span>
                    </div>
                    <div className='d-flex align-items-center gap-3'>
                      <div className='bg-white p-3 rounded border'>
                        <QRCodeCanvas
                          value={pageUrl}
                          size={120}
                          marginSize={0}
                        />
                      </div>
                      <div className='d-flex flex-column'>
                        <button
                          type='button'
                          className='btn btn-sm btn-light-primary'
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(pageUrl);
                              setIsLinkCopied(true);
                              setTimeout(() => setIsLinkCopied(false), 2000);
                            } catch {
                              setIsLinkCopied(false);
                            }
                          }}
                        >
                          <KTIcon
                            iconName={isLinkCopied ? 'check' : 'copy'}
                            className='fs-3 me-2'
                          />
                          {isLinkCopied
                            ? t('Copied', { defaultValue: 'Copied' })
                            : t('Copy link', { defaultValue: 'Copy link' })}
                        </button>
                        <span
                          className='text-muted mt-2'
                          style={{ maxWidth: 260, wordBreak: 'break-all' }}
                        >
                          {pageUrl}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {image && (
                <div className='col-12'>
                  <div
                    className='bgi-no-repeat bgi-position-x-center bgi-size-cover card-rounded border'
                    style={{ backgroundImage: `url(${image})`, height: 220 }}
                  />
                </div>
              )}

              <div className='col-12'>
                <Formik<CheckoutFormValues>
                  validationSchema={formSchema.omit(excludes)}
                  initialValues={{
                    country: 'CM',
                    service: 'ORANGE',
                    line_items: line_items?.map((product) => ({
                      product: String(product.product.id),
                      quantity: product.quantity,
                    })),
                    amount: defaultTotalAmount ?? 0,
                    payer: customerPrefill.phone,
                    full_name: {
                      first_name: customerPrefill.firstName,
                      last_name: customerPrefill.lastName,
                    },
                    contact: {
                      email: urlParams.email ?? customerPrefill.email,
                      phone_number:
                        urlParams.phone_number ?? customerPrefill.phone,
                    },
                    address: {
                      country: String(customerPrefill.country || 'CM'),
                      region: String(customerPrefill.region ?? ''),
                      town: String(customerPrefill.town ?? ''),
                      line1: String(customerPrefill.address ?? ''),
                    },
                    terms_of_service: false,
                    promotions:
                      options?.consentCollection?.promotions === 'auto',
                    coupon: options?.discounts?.coupon ?? '',
                    promotion_code: options?.discounts?.promotion_code ?? '',
                    escrow_mode:
                      securePayMode === 'mandatory' ||
                      (securePayMode === 'optional' && securePayAvailable),
                  }}
                  onSubmit={(
                    values: CheckoutFormValues,
                    formikHelpers: FormikHelpers<CheckoutFormValues>
                  ) => {
                    setShowToast({ display: true, service: values.service });
                    onSubmit(values, formikHelpers);
                  }}
                >
                  {({ isSubmitting, isValid, touched, errors, values }) => {
                    const availablePricing =
                      paymentPricing[values.country ?? 'CM'] ?? [];
                    const providers = new Set(
                      availablePricing.map((p) => p.provider)
                    );
                    const providerOptions = Array.from(providers)
                      .map((provider) => ({
                        value: provider,
                        label: configs.providers[provider]?.service_name,
                        image: providerLogos[configs.providers[provider].key],
                      }))
                      .filter((option) => Boolean(option.label));
                    const subtotalAmount =
                      object == 'checkout'
                        ? (totalAmount ?? 0)
                        : amountOption.type === 'freeType'
                          ? amountOption.suggested &&
                            amountOption.editable === false
                            ? (amountOption.suggested ?? 0)
                            : (values.amount ?? 0)
                          : computeProductsTotal(values.line_items);
                    const securePayEnabled = securePayAvailable
                      ? securePayMode === 'mandatory'
                        ? true
                        : securePayMode === 'optional'
                          ? Boolean(values.escrow_mode)
                          : false
                      : false;
                    const extraFeeAmount =
                      computeExtraFeeAmount(subtotalAmount);
                    const escrowFeeAmount =
                      computeEscrowFeeAmount(subtotalAmount, securePayEnabled);
                    const payableAmount =
                      parseFloat(String(subtotalAmount)) +
                      parseFloat(String(extraFeeAmount)) +
                      parseFloat(String(escrowFeeAmount)) +
                      parseFloat(String(deliveryFees ?? 0));
                    const termsRequired =
                      options?.consentCollection?.terms_of_service ===
                      'required';
                    const submitBlocked =
                      isSubmitting ||
                      !isValid ||
                      isExpired ||
                      !values.service ||
                      !values.payer ||
                      (termsRequired && !values.terms_of_service);
                    const submitHint = !values.service
                      ? t('Please select a payment provider', {
                          defaultValue: 'Please select a payment provider',
                        })
                      : !values.payer
                        ? t('Enter your phone number to continue', {
                            defaultValue: 'Enter your phone number to continue',
                          })
                        : termsRequired && !values.terms_of_service
                          ? t('Please accept the terms to continue', {
                              defaultValue:
                                'Please accept the terms to continue',
                            })
                          : null;
                    const customerMissingCount = (() => {
                      if (!options?.collectCustomerInfo) return 0;

                      let missing = 0;
                      if (!values.full_name?.last_name?.trim()) missing += 1;

                      if (options.requiredContactInfo) {
                        const hasEmail = Boolean(values.contact?.email?.trim());
                        const hasPhone = Boolean(
                          values.contact?.phone_number?.trim()
                        );
                        if (!hasEmail && !hasPhone) missing += 1;
                      }

                      if (options.collectCustomerAddress) {
                        if (!values.address?.country?.trim()) missing += 1;
                        if (!values.address?.town?.trim()) missing += 1;
                      }

                      if (options.customFields?.length) {
                        const customValues = Array.isArray(values.custom_fields)
                          ? values.custom_fields
                          : [];
                        options.customFields.forEach((field, i) => {
                          if (
                            !field.optional &&
                            !String(customValues[i] ?? '').trim()
                          ) {
                            missing += 1;
                          }
                        });
                      }

                      return missing;
                    })();

                    return (
                      <Form noValidate>
                        <div className='row g-6'>
                          <div className='col-lg-7'>
                            <div className='d-flex align-items-center justify-content-between flex-wrap gap-3 p-4 border rounded mb-6'>
                              <div className='d-flex align-items-center'>
                                {application && (
                                  <>
                                    {application.logo_url ? (
                                      <img
                                        src={application.logo_url}
                                        alt={application.name}
                                        className='rounded-circle h-40px w-40px me-3'
                                        style={{ objectFit: 'cover' }}
                                      />
                                    ) : (
                                      <div className='btn btn-icon btn-light h-40px w-40px me-3'>
                                        <span className='fw-bold text-gray-700'>
                                          {application.name
                                            .slice(0, 2)
                                            .toUpperCase()}
                                        </span>
                                      </div>
                                    )}
                                    <div className='d-flex flex-column'>
                                      <span className='fw-semibold text-gray-900'>
                                        {application.name}
                                      </span>
                                      <span className='text-muted'>
                                        {t('Secure checkout', {
                                          defaultValue: 'Secure checkout',
                                        })}
                                      </span>
                                    </div>
                                  </>
                                )}
                              </div>
                              <div className='d-flex align-items-center gap-2'>
                                <span className='badge badge-light-success'>
                                  {t('Encrypted', {
                                    defaultValue: 'Encrypted',
                                  })}
                                </span>
                                {securePayEnabled && (
                                  <span className='badge badge-light-primary'>
                                    {t('Secure payment protection')}
                                  </span>
                                )}
                                <span
                                  className={clsx(
                                    'badge',
                                    brandingSettings?.primary_color
                                      ? ''
                                      : 'badge-light-info'
                                  )}
                                  style={accentBadgeStyle}
                                >
                                  {t('Asynchronous', {
                                    defaultValue: 'Asynchronous',
                                  })}
                                </span>
                              </div>
                            </div>

                            <div
                              className='p-4 border rounded mb-6'
                              style={cardTheme}
                            >
                              <div className='d-flex align-items-center gap-2 mb-4'>
                                <span style={stepBadgeStyle}>1</span>
                                <h4 className='m-0' style={headingStyle}>
                                  {t('Order summary', {
                                    defaultValue: 'Order summary',
                                  })}
                                </h4>
                              </div>

                              {amountOption.type === 'freeType' ? (
                                <div className='mt-4'>
                                  {amountOption.suggested &&
                                  amountOption.editable === false ? (
                                    <div className='d-flex flex-column'>
                                      <span className='text-gray-900 fw-bold fs-2hx'>
                                        {t('intlCurrency', {
                                          value: amountOption.suggested!,
                                          currency: currency ?? 'XAF',
                                        })}
                                      </span>
                                      <span className='text-muted'>
                                        {t('Fixed amount')}
                                      </span>
                                      <Field type={'hidden'} name={'amount'} />
                                    </div>
                                  ) : (
                                    <FormField
                                      type={'amount'}
                                      name={'amount'}
                                      error={errors.amount}
                                      touched={touched.amount}
                                      placeholder={t('Amount')}
                                      readOnly={isDemo}
                                      prefix={`${currency ?? 'XAF'} `}
                                      onValueChanged={(value) => {
                                        const subtotal =
                                          parseInt(value as string) || 0;
                                        setTotalAmount(
                                          subtotal +
                                            computeExtraFeeAmount(subtotal)
                                        );
                                      }}
                                    />
                                  )}
                                </div>
                              ) : (
                                <table className='table align-middle table-row-dashed fs-6 gy-5 mb-0'>
                                  <tbody className='fw-semibold text-gray-600'>
                                    {values.line_items?.map((prod, i) => {
                                      let productError: FormikErrors<
                                        Yup.InferType<typeof productSchema>
                                      > = {};
                                      let productTouched: FormikTouched<
                                        Yup.InferType<typeof productSchema>
                                      > = {};
                                      const lineItemErrors = Array.isArray(
                                        errors.line_items
                                      )
                                        ? errors.line_items
                                        : [];
                                      if (lineItemErrors.length > i) {
                                        productError =
                                          (lineItemErrors[i] as FormikErrors<
                                            Yup.InferType<typeof productSchema>
                                          >) ?? {};
                                      }
                                      const lineItemTouched = Array.isArray(
                                        touched.line_items
                                      )
                                        ? touched.line_items
                                        : [];
                                      if (
                                        lineItemTouched.length > i &&
                                        typeof lineItemTouched[i] === 'object'
                                      ) {
                                        productTouched =
                                          (lineItemTouched[i] as FormikTouched<
                                            Yup.InferType<typeof productSchema>
                                          >) ?? {};
                                      }

                                      const product = line_items?.[i];
                                      if (!product) return null;

                                      return (
                                        <tr key={`prod-${i}`}>
                                          <td>
                                            <div className='d-flex align-items-center'>
                                              <Field
                                                type={'hidden'}
                                                name={`line_items[${i}].product`}
                                                errors={errors}
                                                touched={touched}
                                              />
                                              {product.product.image_url && (
                                                <a
                                                  href='#'
                                                  className='symbol symbol-50px'
                                                >
                                                  <span
                                                    className='symbol-label'
                                                    style={{
                                                      backgroundImage: `url(${product.product.image_url})`,
                                                    }}
                                                  />
                                                </a>
                                              )}
                                              <div className='ms-5'>
                                                <div className='fw-semibold text-gray-900'>
                                                  {product.product.name}
                                                </div>
                                                <div className='fw-light fs-7'>
                                                  {product.product.description}
                                                </div>
                                                {product.adjustable ? (
                                                  <div
                                                    className={'mt-2'}
                                                    style={{ maxWidth: 140 }}
                                                  >
                                                    <FormField
                                                      type={'number'}
                                                      name={`line_items[${i}].quantity`}
                                                      error={
                                                        productError?.quantity
                                                      }
                                                      touched={
                                                        productTouched?.quantity
                                                      }
                                                      size={'sm'}
                                                      marginBottom={0}
                                                      onChange={(value) => {
                                                        const nextQuantity =
                                                          parseInt(
                                                            String(
                                                              value ?? '0'
                                                            ),
                                                            10
                                                          ) || 0;
                                                        const subtotal =
                                                          computeProductsTotal(
                                                            (
                                                              values.line_items ??
                                                              []
                                                            ).map((p) => ({
                                                              ...p,
                                                              quantity:
                                                                p.product ==
                                                                String(
                                                                  product
                                                                    .product.id
                                                                )
                                                                  ? nextQuantity
                                                                  : p.quantity,
                                                            }))
                                                          );
                                                        setTotalAmount(
                                                          subtotal +
                                                            computeExtraFeeAmount(
                                                              subtotal
                                                            )
                                                        );
                                                      }}
                                                    />
                                                  </div>
                                                ) : (
                                                  <div className='mt-1'>
                                                    <Field
                                                      type={'hidden'}
                                                      name={`line_items[${i}].quantity`}
                                                      error={
                                                        productError?.quantity
                                                      }
                                                      touched={
                                                        productTouched?.quantity
                                                      }
                                                    />
                                                    <span className='fw-light text-gray-700'>
                                                      {t('Qty')}{' '}
                                                      {t('intlNumber', {
                                                        value: prod.quantity,
                                                      })}
                                                    </span>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          </td>
                                          <td className='text-end'>
                                            <div className='text-gray-900'>
                                              {t('intlCurrency', {
                                                value:
                                                  prod.quantity *
                                                  (product.unit_amount ?? 0),
                                                currency:
                                                  product.product
                                                    .default_price_currency ??
                                                  'XAF',
                                              })}
                                            </div>
                                            {product.product.default_price && (
                                              <div className='mt-2 fs-7 fw-light'>
                                                {t('intlCurrency', {
                                                  value:
                                                    product.product
                                                      .default_price,
                                                  currency:
                                                    product.product
                                                      .default_price_currency ??
                                                    'XAF',
                                                })}{' '}
                                                {t('each')}
                                              </div>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              )}

                              <div className='separator my-4' />

                              <div className='d-flex align-items-center justify-content-between mb-2'>
                                <span className='text-muted'>
                                  {t('Subtotal', { defaultValue: 'Subtotal' })}
                                </span>
                                <span className='text-gray-800'>
                                  {t('intlCurrency', {
                                    value: subtotalAmount,
                                    currency: currency,
                                  })}
                                </span>
                              </div>

                              <div className={'mb-2'} style={{
                                borderBottom: '1px dashed var(--bs-border-color)'
                              }}></div>

                              {(deliveryFees && deliveryFees > 0) && (
                                <div className='d-flex align-items-center justify-content-between mb-2'>
                                  <span className='text-muted'>
                                    {t('Delivery Fees')}
                                  </span>
                                  <span className='text-gray-800'>
                                    {t('intlCurrency', {
                                      value: deliveryFees,
                                      currency: currency,
                                    })}
                                  </span>
                                </div>
                              )}

                              {extraFees?.enabled && (
                                <div className='d-flex align-items-center justify-content-between mb-2'>
                                  <span className='text-muted'>
                                    {t('Extra fees', {
                                      defaultValue: 'Extra fees',
                                    })}
                                    {extraFees.reason
                                      ? ` - ${extraFees.reason}`
                                      : ''}
                                    {extraFees.type === 'PERCENTAGE'
                                      ? ` (${extraFees.value ?? 0}%)`
                                      : ''}
                                  </span>
                                  <span className='text-gray-800'>
                                    {t('intlCurrency', {
                                      value: extraFeeAmount,
                                      currency: currency,
                                    })}
                                  </span>
                                </div>
                              )}

                              {escrowFeeAmount > 0 && (
                                <div className='d-flex align-items-center justify-content-between mb-2'>
                                  <span className='text-muted'>
                                    {t('Secure Pay fee')}
                                  </span>
                                  <span className='text-gray-800'>
                                    {t('intlCurrency', {
                                      value: escrowFeeAmount,
                                      currency: currency,
                                    })}
                                  </span>
                                </div>
                              )}

                              <div className={'mb-2'} style={{
                                borderBottom: '1px dashed var(--bs-border-color)'
                              }}></div>

                              <div className='d-flex align-items-center justify-content-between mb-2'>
                                <span className='fw-semibold text-gray-900'>
                                  {t('Total', { defaultValue: 'Total' })}
                                </span>
                                <span className='fw-bold text-gray-900'>
                                  {t('intlCurrency', {
                                    value: payableAmount,
                                    currency: currency,
                                  })}
                                </span>
                              </div>

                              {securePayEnabled && (
                                <div className='alert alert-primary d-flex flex-column gap-2 p-3 mt-3 mb-2'>
                                  <div className='d-flex align-items-center gap-2'>
                                    <KTSVG
                                      path='/assets/icons/shield-tick.svg'
                                      className='fs-3 text-primary'
                                    />
                                    <span className='fw-semibold text-gray-900'>
                                      {t('Secure payment protection')}
                                    </span>
                                    <span className='badge badge-light-primary'>
                                      {securePayModeLabel}
                                    </span>
                                  </div>
                                  <div className='text-muted fs-7'>
                                    {securePayIsMandatory
                                      ? t('Secure payment mandatory message', {
                                          defaultValue:
                                            'This payment is protected by secure pay. Funds are held until transaction conditions are met.',
                                        })
                                      : t('Secure payment optional message', {
                                          defaultValue:
                                            'Secure pay is available for this transaction to increase trust between buyer and seller.',
                                        })}
                                  </div>
                                  <div className='d-flex flex-column gap-1 fs-7 text-gray-700'>
                                    <div className='d-flex justify-content-between'>
                                      <span>{t('Escrow fee payer')}</span>
                                      <span className='fw-semibold'>
                                        {securePayFeePayerLabel}
                                      </span>
                                    </div>
                                    <div className='d-flex justify-content-between'>
                                      <span>
                                        {t('Delivery confirmation required')}
                                      </span>
                                      <span className='fw-semibold'>
                                        {securePayDeliveryRequired
                                          ? t('Yes', { defaultValue: 'Yes' })
                                          : t('No', { defaultValue: 'No' })}
                                      </span>
                                    </div>
                                    {securePayCommitmentFee > 0 && (
                                      <div className='d-flex justify-content-between'>
                                        <span>{t('Commitment fee')}</span>
                                        <span className='fw-semibold'>
                                          {t('intlCurrency', {
                                            value: securePayCommitmentFee,
                                            currency,
                                          })}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                              {metadata?.order_id && (
                                <div className='d-flex align-items-center justify-content-between mb-2'>
                                  <span className='text-muted'>
                                    {t('Reference', {
                                      defaultValue: 'Reference',
                                    })}
                                  </span>
                                  <span className='text-gray-700'>
                                    {metadata?.order_id}
                                  </span>
                                </div>
                              )}
                              {options?.discounts?.coupon && (
                                <div className='d-flex align-items-center justify-content-between mb-2'>
                                  <span className='text-muted'>
                                    {t('Coupon', { defaultValue: 'Coupon' })}
                                  </span>
                                  <span className='badge badge-light-success'>
                                    {options.discounts.coupon}
                                  </span>
                                </div>
                              )}
                              {options?.discounts?.promotion_code && (
                                <div className='d-flex align-items-center justify-content-between mb-2'>
                                  <span className='text-muted'>
                                    {t('Promotion code', {
                                      defaultValue: 'Promotion code',
                                    })}
                                  </span>
                                  <span className='badge badge-light-info'>
                                    {options.discounts.promotion_code}
                                  </span>
                                </div>
                              )}
                            </div>

                            {options?.collectCustomerInfo && (
                              <div className='p-4 border rounded'>
                                <div className='d-flex align-items-center gap-2 mb-4'>
                                  <span style={stepBadgeStyle}>3</span>
                                  <h5
                                    className='m-0 text-gray-900'
                                    style={headingStyle}
                                  >
                                    {t('Customer details', {
                                      defaultValue: 'Customer details',
                                    })}
                                  </h5>
                                </div>

                                {options.requiredContactInfo && (
                                  <div className='mb-5'>
                                    <h5
                                      className='mb-2 text-gray-900'
                                      style={headingStyle}
                                    >
                                      {t('Contact Information')}
                                    </h5>
                                    <FormField
                                      type={'contact'}
                                      name={'contact'}
                                      error={errors.contact}
                                      touched={touched.contact}
                                      readOnly={isDemo}
                                    />
                                  </div>
                                )}

                                <div className='mb-5'>
                                  <h5
                                    className='mb-2 text-gray-900'
                                    style={headingStyle}
                                  >
                                    {t('Identity')}
                                  </h5>
                                  <FormField
                                    type={'full_name'}
                                    name={'full_name'}
                                    error={errors.full_name}
                                    touched={touched.full_name}
                                    readOnly={isDemo}
                                  />
                                </div>

                                {options.collectCustomerAddress && (
                                  <div className='mb-0'>
                                    <h5
                                      className='mb-2 text-gray-900'
                                      style={headingStyle}
                                    >
                                      {t('Address')}
                                    </h5>
                                    <FormField
                                      type={'address'}
                                      name={'address'}
                                      error={errors.address}
                                      touched={touched.address}
                                      readOnly={isDemo}
                                    />
                                  </div>
                                )}

                                {options.customFields?.length ? (
                                  <div className='mt-6'>
                                    <h5
                                      className='mb-3 text-gray-900'
                                      style={headingStyle}
                                    >
                                      {t('Additional information')}
                                    </h5>
                                    {options.customFields.map((field, i) => (
                                      <FormField
                                        key={`custom-${i}`}
                                        type={field.type}
                                        name={`custom_fields[${i}]`}
                                        error={
                                          Array.isArray(errors.custom_fields)
                                            ? errors.custom_fields[i]
                                            : undefined
                                        }
                                        touched={
                                          Array.isArray(touched.custom_fields)
                                            ? touched.custom_fields[i] === true
                                            : undefined
                                        }
                                        label={field.label}
                                        required={!field.optional}
                                        validate={(value) => {
                                          if (!field.optional && !value)
                                            return t('Field_is_required');
                                          if (
                                            field.type === 'number' &&
                                            value &&
                                            !/^\d+$/.test(value as string)
                                          )
                                            return t('Invalid number');
                                          if (!value) return;

                                          if (field.set_limit) {
                                            if (field.type == 'text') {
                                              if (
                                                field.limit_min &&
                                                (value as string).length <
                                                  field.limit_min
                                              )
                                                return t('Minimum_characters', {
                                                  min: field.limit_min,
                                                });
                                              if (
                                                field.limit_max &&
                                                (value as string).length >
                                                  field.limit_max
                                              )
                                                return t('Maximum_characters', {
                                                  max: field.limit_max,
                                                });
                                            } else {
                                              if (
                                                field.limit_min &&
                                                (value as unknown as number) <
                                                  field.limit_min
                                              )
                                                return t('Minimum_value', {
                                                  min: field.limit_min,
                                                });
                                              if (
                                                field.limit_max &&
                                                (value as unknown as number) >
                                                  field.limit_max
                                              )
                                                return t('Maximum_value', {
                                                  max: field.limit_max,
                                                });
                                            }
                                          }
                                        }}
                                      />
                                    ))}
                                  </div>
                                ) : null}
                              </div>
                            )}
                          </div>

                          <div className='col-lg-5'>
                            <div
                              className='position-sticky'
                              style={{ top: 16 }}
                            >
                              {customer && (
                                <div className='d-flex flex-column border rounded p-3 mb-5 bg-light-primary'>
                                  <div className='fw-semibold text-gray-900 mb-2'>
                                    {t('Customer', {
                                      defaultValue: 'Known customer',
                                    })}
                                  </div>
                                  {customerPrefill.displayName && (
                                    <div className='text-gray-800 fs-7'>
                                      {customerPrefill.displayName}
                                    </div>
                                  )}
                                  <div className='d-flex flex-wrap gap-3 text-muted fs-8'>
                                    {customerPrefill.email && (
                                      <span>{customerPrefill.email}</span>
                                    )}
                                    {customerPrefill.phone && (
                                      <span>{customerPrefill.phone}</span>
                                    )}
                                  </div>
                                </div>
                              )}

                              <div
                                className='p-4 border rounded'
                                style={cardTheme}
                              >
                                <div className='d-flex align-items-center gap-2 mb-3'>
                                  <span style={stepBadgeStyle}>2</span>
                                  <h5
                                    className='m-0 text-gray-900'
                                    style={headingStyle}
                                  >
                                    {t('Payment Method')}
                                  </h5>
                                </div>
                                <div className='text-muted fs-7 mb-4'>
                                  {t(
                                    'Choose your country, provider, and phone number to complete payment.',
                                    {
                                      defaultValue:
                                        'Choose your country, provider, and phone number to complete payment.',
                                    }
                                  )}
                                </div>
                                <div className='mb-4'>
                                  <FormField
                                    type={'select2'}
                                    name={'country'}
                                    error={errors.country}
                                    touched={touched.country}
                                    options={(application?.countries ?? []).map(
                                      (c: string) => ({
                                        value: c,
                                        label: countries[c].name,
                                        image: countryFlagUrl(c),
                                      })
                                    )}
                                    readOnly={isDemo}
                                  />
                                </div>

                                <div className='mb-4'>
                                  <FormField
                                    type={'megaOption'}
                                    name={'service'}
                                    error={errors.service}
                                    touched={touched.service}
                                    options={providerOptions}
                                    readOnly={isDemo}
                                  />
                                </div>

                                <div className='mb-4'>
                                  <FormField
                                    type={'phone'}
                                    name={'payer'}
                                    error={errors.payer}
                                    touched={touched.payer}
                                    placeholder={'670000000'}
                                    readOnly={isDemo}
                                    showCountryCode={false}
                                    country={values.country}
                                  />
                                  <div className='text-muted fs-8 mt-2'>
                                    {providerUssd[values.service ?? '']
                                      ? t(
                                          'After submit, validate on {{code}}',
                                          {
                                            defaultValue:
                                              'After submit, validate on {{code}}',
                                            code: providerUssd[
                                              values.service ?? ''
                                            ],
                                          }
                                        )
                                      : t('Use a valid mobile money number', {
                                          defaultValue:
                                            'Use a valid mobile money number',
                                        })}
                                  </div>
                                </div>

                                {securePayOptional && (
                                  <div className='mb-4 p-3 border rounded bg-light-primary'>
                                    <FormField
                                      type={'switch'}
                                      name={'escrow_mode'}
                                      error={errors.escrow_mode}
                                      touched={touched.escrow_mode}
                                      label={t('Pay with secure payment protection')}
                                      readOnly={isDemo}
                                    />
                                    <div className='text-muted fs-8 mt-2'>
                                      {t(
                                        'Recommended: secure pay keeps funds protected until transaction conditions are met.',
                                      )}
                                    </div>
                                  </div>
                                )}

                                <Field
                                  type={'hidden'}
                                  name={'coupon'}
                                  value={options?.discounts?.coupon ?? ''}
                                />
                                <Field
                                  type={'hidden'}
                                  name={'promotion_code'}
                                  value={
                                    options?.discounts?.promotion_code ?? ''
                                  }
                                />

                                {options?.consentCollection
                                  ?.terms_of_service === 'required' && (
                                  <div className='mb-4'>
                                    <FormField
                                      type={'switch'}
                                      name={'terms_of_service'}
                                      error={errors.terms_of_service}
                                      touched={touched.terms_of_service}
                                      label={
                                        options?.customText
                                          ?.terms_of_service_acceptance
                                          ? t(
                                              options.customText
                                                .terms_of_service_acceptance,
                                              {
                                                defaultValue:
                                                  options.customText
                                                    .terms_of_service_acceptance,
                                              }
                                            )
                                          : t(
                                              'I agree to the terms of service',
                                              {
                                                defaultValue:
                                                  'I agree to the terms of service',
                                              }
                                            )
                                      }
                                      readOnly={isDemo}
                                    />
                                  </div>
                                )}

                                {options?.consentCollection?.promotions ===
                                  'auto' && (
                                  <div className='mb-4'>
                                    <FormField
                                      type={'switch'}
                                      name={'promotions'}
                                      error={errors.promotions}
                                      touched={touched.promotions}
                                      label={t(
                                        'I want to receive promotions and updates',
                                        {
                                          defaultValue:
                                            'I want to receive promotions and updates',
                                        }
                                      )}
                                      readOnly={isDemo}
                                    />
                                  </div>
                                )}

                                {options?.collectCustomerInfo && (
                                  <div
                                    className='d-flex align-items-center justify-content-between mb-4 p-3 border rounded bg-light-primary'
                                    style={{
                                      borderColor:
                                        brandingSettings?.primary_color,
                                    }}
                                  >
                                    <span className='fw-semibold text-gray-800'>
                                      {t('Customer profile')}
                                    </span>
                                    <span
                                      className={clsx(
                                        'badge',
                                        customerMissingCount > 0
                                          ? 'badge-light-warning'
                                          : 'badge-light-success'
                                      )}
                                    >
                                      {customerMissingCount > 0
                                        ? t('Missing_fields_count', {
                                            count: customerMissingCount,
                                            defaultValue: `${customerMissingCount} field(s) missing`,
                                          })
                                        : t('Complete', {
                                            defaultValue: 'Complete',
                                          })}
                                    </span>
                                  </div>
                                )}

                                {securePayEnabled && (
                                  <div className='d-flex align-items-start gap-2 mb-4 p-3 border rounded bg-light-primary'>
                                    <KTSVG
                                      path='/assets/icons/shield-tick.svg'
                                      className='fs-4 text-primary mt-1'
                                    />
                                    <div className='d-flex flex-column'>
                                      <span className='fw-semibold text-gray-900'>
                                        {t('Protected by MeSomb secure pay')}
                                      </span>
                                      <span className='text-muted fs-8'>
                                        {securePayDeliveryRequired
                                          ? t('Secure payment delivery required message', {
                                              defaultValue:
                                                'Your payment remains protected until delivery confirmation is validated.',
                                            })
                                          : t('Secure payment generic reassurance', {
                                              defaultValue:
                                                'Your payment is processed with escrow safeguards for better trust and dispute prevention.',
                                            })}
                                      </span>
                                    </div>
                                  </div>
                                )}

                                <ButtonIndicator
                                  type={'submit'}
                                  label={`${ctaLabel} ${t('intlCurrency', {
                                    value: payableAmount,
                                    currency: currency,
                                  })}`}
                                  className={clsx('w-100', ctaColor ? 'text-white' : 'btn-primary')}
                                  style={
                                    ctaColor
                                      ? {
                                          backgroundColor: ctaColor,
                                          borderColor: ctaColor,
                                        }
                                      : undefined
                                  }
                                  loading={isSubmitting}
                                  disabled={submitBlocked}
                                />

                                {onCancel && (
                                  <ButtonIndicator
                                    type={'button'}
                                    label={t('Cancel')}
                                    className={'btn btn-light w-100 mt-2'}
                                    loading={isCancelling}
                                    onClick={async () => {
                                      const confirmed = window.confirm(
                                        t('Are you sure you want to cancel this payment?', {
                                          defaultValue:
                                            'Are you sure you want to cancel this payment?',
                                        })
                                      );
                                      if (confirmed) {
                                        try {
                                          setIsCancelling(true)
                                          await onCancel();
                                        } finally {
                                          setIsCancelling(false)
                                        }
                                      }
                                    }}
                                  />
                                )}

                                {submitHint && (
                                  <div className='text-warning fs-8 mt-2'>
                                    {submitHint}
                                  </div>
                                )}

                                <div className='mt-4 text-muted fs-7'>
                                  {options?.customText?.after_submit
                                    ? t(options.customText.after_submit, {
                                        defaultValue:
                                          options.customText.after_submit,
                                      })
                                    : t(
                                        'By continuing, you confirm this payment is authorized by you.',
                                        {
                                          defaultValue:
                                            'By continuing, you confirm this payment is authorized by you.',
                                        }
                                      )}
                                </div>

                                <div className='d-flex align-items-center mt-3 text-muted fs-8'>
                                  <KTSVG
                                    path='/assets/icons/shield-tick.svg'
                                    className='fs-5 me-2'
                                  />
                                  {t(
                                    'Payments are secured and processed in real time.',
                                    {
                                      defaultValue:
                                        'Payments are secured and processed in real time.',
                                    }
                                  )}
                                </div>

                                {showToast.display && (
                                  <Toast
                                    onClose={() =>
                                      setShowToast({ display: false })
                                    }
                                    show={showToast.display}
                                    delay={5000}
                                    autohide={true}
                                    bg={'light'}
                                    animation={true}
                                    style={{ width: '100%' }}
                                    className={'mt-3'}
                                  >
                                    <Toast.Body className={'p-2 fs-6'}>
                                      {options?.customText?.after_submit
                                        ? t(options.customText.after_submit, {
                                            defaultValue:
                                              options.customText.after_submit,
                                          })
                                        : t('Check_phone_validation', {
                                            ussd: providerUssd[
                                              showToast.service ?? ''
                                            ],
                                          })}
                                    </Toast.Body>
                                  </Toast>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </Form>
                    );
                  }}
                </Formik>
              </div>
            </div>
          </div>
        </KTCardBody>
      </KTCard>
      <div className='d-flex align-items-center justify-content-center mt-5 text-muted fs-8'>
        <img
          src={'/logo.png'}
          width={20}
          alt='MeSomb'
          className='h-12px w-12px me-1 opacity-75'
        />
        {t('Powered by')} <span className='fw-semibold ms-1'>MeSomb</span>
      </div>
    </>
  );
}

export default PayWidget;
